import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErroApi } from '../../api';
import {
  campanhaEditavel, codigoDaCampanha, criarModeloDeImportacao, importarArquivosCptu,
  lerCabecalhoDoCsv, listarModelosDeImportacao, MOTIVOS_DE_PARADA, verCampanha,
  type AvisoDaConferencia, type CabecalhoDoCsv, type CampanhaCompleta, type ColunaDoMapa,
  type CampoDaAnterior, type Conferencia, type ConferenciaDaVertical, type DecisaoDaVertical,
  type Decisoes, type MapaDeColunas, type ModeloDeImportacao, type MotivoDeParada,
} from '../../api/cptu';
import { useCaminho } from '../../Caminho';
import {
  data, dataDoDia, faixaProfundidade, numero, paraCampo, paraNumero, plural,
} from '../../formato';
import { useSessao } from '../../sessao';
import { CONE_NA_DATA, RESULTADO_DA_CONFERENCIA_CPTU } from '../../situacoes';
import {
  Aviso, BarraDeAvanco, Campo, CampoNumerico, Carregando, ErroDaApi, SeloDaEscada,
  useEstreito, ZonaDeArquivo,
} from '../../ui';
import { EscolhaDoCone } from './CampanhaVerticais';
import { motivoDaCampanhaParada, PERMISSAO_DE_EDITAR_CPTU, SeloDaCampanha } from './CampanhaMoldura';

/**
 * A importação dos arquivos do CPTu — onda 312, Tarefa 312.5 (spec 07 §4;
 * R4 a R8; RN-253). As pranchetas «Importar» do canvas aprovado.
 *
 * Os arquivos de uma campanha entram juntos (os treze do 2609350 cabem com
 * folga): a planilha do Pagani de cada vertical e de cada dissipação, um
 * CSV/TXT de outro equipamento (com o mapa de colunas) ou o projeto do
 * CPeT-IT. O sistema reconhece cada um pelo conteúdo e CONFERE antes de
 * gravar — «Conferir sem gravar» não grava linha nem arquivo nenhum.
 *
 * A conferência informa (R4): um cartão por vertical com o ponto da OS que
 * ela usa ou cria (R7), a data — com a escolha, quando o arquivo é ambíguo
 * (R5) —, as leituras, o cone com o selo apto/impedido, o NA, a cota, a
 * coordenada, o encerramento, e os avisos (atenção com o símbolo e a
 * palavra; a inclinação e a deriva dos zeros como informação). Nenhum aviso
 * bloqueia a gravação. O que o engenheiro muda vai como DECISÃO: o servidor
 * refaz a conferência ao gravar.
 *
 * Os itens idênticos ao que já está gravado chegam desmarcados (a vertical
 * idêntica o servidor recusa; a dissipação idêntica, numa importação que
 * traz algo novo, ele mantém), e a cópia de um arquivo desta importação (os
 * mesmos bytes com outro nome) também — o primeiro do par fica marcado. O
 * desmarcado por isso mostra só o porquê; marcado de novo, ele trava o
 * «Importar» com o motivo, e a barra conta à parte os avisos que bloqueiam
 * (agent-browser da 312). A vertical que substitui outra (R8) pede o
 * motivo, e chega com o que o engenheiro informou na substituída onde o
 * arquivo novo não traz — o NA, a cota, a coordenada, o cone, o pré-furo, o
 * encerramento, o operador, a data e as observações, cada um com a dica «da
 * importação anterior» (revisão final da 312); a pessoa muda antes de
 * gravar. Os 409 do servidor («mudou desde a conferência», «o mesmo arquivo
 * já está gravado») aparecem aqui, com o botão de conferir de novo.
 *
 * Um pedido só, e não lotes de dez: a dissipação se liga à vertical NO MESMO
 * envio (até 30 arquivos — o `LIMITE_DE_ARQUIVOS` da rota —, somando até 60
 * MB). No celular, a importação não existe: ela é feita no computador.
 */

/** O limite da rota (`LIMITE_DE_ARQUIVOS`, `importacao.service.ts`): um envio só, até 30 arquivos. */
const ARQUIVOS_POR_IMPORTACAO = 30;

/**
 * O envio inteiro: 60 MB (o `LIMITE_DA_IMPORTACAO_CPTU` do `main.ts` da API, abaixo do `client_max_body_size
 * 64m` do nginx) — revisão final da 312. A tela soma os tamanhos antes de mandar; e o 413 que o nginx devolve
 * em HTML (que a tela não lê) aparece com a mesma frase da API.
 */
const BYTES_POR_IMPORTACAO = 60 * 1024 * 1024;
const RECADO_DA_IMPORTACAO_GRANDE = 'A importação passa de 60 MB: envie os arquivos em duas ou mais vezes (até 30 '
  + 'arquivos de até 40 MB cada, somando até 60 MB por importação).';
const somaDosTamanhos = (fs: File[]) => fs.reduce((soma, f) => soma + f.size, 0);

const ACEITA = '.xlsx,.xls,.csv,.txt,.cpt';
const ehCsv = (f: File) => /\.(csv|txt)$/i.test(f.name);

export function ImportarCptu() {
  const { id = '' } = useParams();
  const { pode, eu } = useSessao();
  const estreito = useEstreito();
  const [c, setC] = useState<CampanhaCompleta | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  /*
   * Montada uma vez no computador, a importação fica montada (agent-browser da 312): a janela que passa por baixo
   * dos 640 px (estreitada, ou com o zoom) mostra o aviso do celular e só ESCONDE a importação — os arquivos, o mapa
   * do CSV e o que já foi decidido na conferência voltam com a janela. Aberta direto no celular, ela nem monta.
   */
  const [montada, setMontada] = useState(false);
  useEffect(() => { if (!estreito) setMontada(true); }, [estreito]);

  useEffect(() => {
    let vivo = true;
    verCampanha(id).then((r) => { if (vivo) setC(r); }).catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [id]);

  useCaminho(c ? [
    { rotulo: 'Campanha', codigo: `${codigoDaCampanha(c.campanha.sequencia)} · OS ${c.os.numero}`, para: `/cptu/${id}` },
    { rotulo: 'Importar' },
  ] : null);

  if (erro && !c) return <ErroDaApi erro={erro} />;
  if (!c) return <Carregando texto="Abrindo a campanha CPTu" />;

  const voltar = <Link className="botao secundario" to={`/cptu/${id}`}>Voltar à campanha</Link>;
  let impedimento: ReactNode = null;
  if (!pode('cptu.editar')) {
    impedimento = (
      <Aviso tipo="info" titulo="Seu perfil consulta as campanhas CPTu e não importa.">
        {eu?.atuandoPor
          ? `Você está atuando como ${eu.nome}, que não importa: volte a ser você na tarja do topo.`
          : `Quem importa tem a permissão «${PERMISSAO_DE_EDITAR_CPTU}» (o gestor de campo e o responsável `
            + 'técnico): peça a quem administra os usuários.'}
      </Aviso>
    );
  } else if (!campanhaEditavel(c.campanha.situacao)) {
    impedimento = (
      <Aviso tipo="info" titulo="Esta campanha não recebe importação.">
        {motivoDaCampanhaParada(c.campanha.situacao)}
      </Aviso>
    );
  }

  return (
    <div className="cptu-importar">
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Importar arquivos do equipamento</h1>
          <p className="sub">
            Campanha <span className="mono">{codigoDaCampanha(c.campanha.sequencia)}</span> · OS{' '}
            <span className="mono">{c.os.numero}</span> · {c.campanha.titulo}{' '}
            <SeloDaCampanha situacao={c.campanha.situacao} />
          </p>
        </div>
        {voltar}
      </div>
      {impedimento ?? (
        <>
          {estreito && (
            <Aviso tipo="info" titulo="A importação é feita no computador.">
              No celular a campanha é consulta: abra esta página no computador, com os arquivos do equipamento à mão.
            </Aviso>
          )}
          {(montada || !estreito) && (
            <div className="cptu-importar-corpo" hidden={estreito}>
              <Importacao c={c} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── O estado do que o engenheiro decide ───────────────────────────── */

interface EdicaoDaVertical {
  chave: string;
  arquivo: string;
  formato: ConferenciaDaVertical['formato'];
  /** O código que o arquivo trouxe (no `.cpt`, é por ele que a decisão acha a vertical). */
  codigoLido: string;
  incluir: boolean;
  codigo: string;
  data: string;
  na: string; naLido: string;
  cota: string; cotaLido: string;
  lat: string; lon: string; coordenadaLida: string;
  parada: MotivoDeParada | ''; paradaLida: MotivoDeParada | '';
  paradaTexto: string; paradaTextoLido: string;
  operador: string; operadorLido: string;
  preFuro: string; preFuroLido: string;
  observacoes: string; observacoesLido: string;
  substituicao: string;
  /** O cone escolhido aqui; ausente = o do arquivo (achado pelo número). */
  cone?: { id: string | null; rotulo: string | null };
}

interface EdicaoDaDissipacao {
  arquivo: string;
  /** O arquivo como foi conferido (nome, tamanho e data): trocado o arquivo, a decisão não vale. */
  assinatura: string;
  profundidade: number;
  /** A vertical que o arquivo diz (o `.cpt` não deixa trocar: a decisão acha a dissipação por ela). */
  verticalLida: string | null;
  travada: boolean;
  incluir: boolean;
  vertical: string;
}

const mesmaProfundidade = (a: number, b: number) => Math.abs(a - b) < 0.0005;
/** O arquivo pelo que o distingue de outro de mesmo nome: o nome, o tamanho e a data. */
const assinaturaDoArquivo = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
/** As assinaturas dos arquivos escolhidos, pelo nome (a zona não deixa dois com o mesmo nome). */
type Assinaturas = ReadonlyMap<string, string>;
const assinaturaDe = (nome: string, sigs: Assinaturas) => sigs.get(nome) ?? nome;
/**
 * A chave de uma vertical: o ARQUIVO (pela assinatura, não pelo nome — o arquivo trocado por outro
 * de mesmo nome não herda a decisão) e, no projeto do CPeT-IT, o código que o arquivo trouxe.
 */
const chaveDaVertical = (v: ConferenciaDaVertical, sigs: Assinaturas) =>
  (v.formato === 'cpeit_cpt' ? `${assinaturaDe(v.arquivo, sigs)}|${v.codigoLido}` : assinaturaDe(v.arquivo, sigs));
const textoDaCoordenada = (lat: string, lon: string) => `${lat}|${lon}`;
/** O código com que a vertical entra (no `.cpt`, o do ponto da OS que a conferência deu). */
const codigoQueEntra = (e: EdicaoDaVertical) => e.codigo.trim();
const mesmaDissipacao = (e: EdicaoDaDissipacao, d: { arquivo: string; profundidade: number; vertical: string | null }, sigs: Assinaturas) =>
  e.assinatura === assinaturaDe(d.arquivo, sigs) && mesmaProfundidade(e.profundidade, d.profundidade)
    && (e.verticalLida === d.vertical || e.vertical === d.vertical);
/** Os códigos e as verticais das dissipações: renomeado um código depois de conferir, confira de novo. */
const assinaturaDasDecisoes = (vs: EdicaoDaVertical[], ds: EdicaoDaDissipacao[]) =>
  JSON.stringify([vs.map((e) => [e.chave, e.codigo.trim()]), ds.map((e) => [e.assinatura, e.profundidade, e.vertical])]);

/** O arquivo idêntico ao que já está gravado: a conferência o marca com o aviso (revisão da 312.3). */
const ehIdentica = (avisos: AvisoDaConferencia[]) =>
  avisos.some((a) => /O mesmo arquivo já está gravado/.test(a.texto) || /é a mesma já gravada/.test(a.texto));

/** Os avisos que dizem por que o cartão chega desmarcado: o arquivo já gravado, ou a cópia de outro desta importação. */
const ehAvisoDoMesmoArquivo = (a: AvisoDaConferencia) =>
  /O mesmo arquivo já está gravado/.test(a.texto) || /tem os mesmos bytes de/.test(a.texto);

/**
 * O primeiro arquivo do par com os mesmos bytes (o `copiaDe` da conferência — no `.cpt`, a mesma vertical também):
 * a cópia que chega depois fica desmarcada enquanto ele entra (agent-browser da 312).
 */
const gemeaDe = (v: ConferenciaDaVertical, todas: ConferenciaDaVertical[]) => (!v.copiaDe ? undefined
  : todas.find((o) => o !== v && o.arquivo === v.copiaDe
    && (v.formato !== 'cpeit_cpt' || o.codigoLido.trim().toLowerCase() === v.codigoLido.trim().toLowerCase())));

/**
 * O que a conferência trouxe vira o ponto de partida da edição — e o «lido» de cada campo: a decisão manda só
 * o que a pessoa mudou. Na reimportação, o que o arquivo não traz já vem da vertical substituída (o servidor
 * usa o mesmo padrão ao gravar).
 */
function novaEdicaoDaVertical(v: ConferenciaDaVertical, chave: string): EdicaoDaVertical {
  const lat = paraCampo(v.coordenada?.latitude, 7);
  const lon = paraCampo(v.coordenada?.longitude, 7);
  const parada = v.encerramento?.motivo ?? '';
  const paradaTexto = v.encerramento?.texto ?? '';
  return {
    chave, arquivo: v.arquivo, formato: v.formato, codigoLido: v.codigoLido,
    incluir: v.ponto.acao !== 'recusar' && !ehIdentica(v.avisos),
    codigo: v.codigo,
    data: v.dataEnsaio ?? v.data.iso ?? '',
    na: paraCampo(v.na, 2), naLido: paraCampo(v.na, 2),
    cota: paraCampo(v.cota, 3), cotaLido: paraCampo(v.cota, 3),
    lat, lon, coordenadaLida: textoDaCoordenada(lat, lon),
    parada, paradaLida: parada, paradaTexto, paradaTextoLido: paradaTexto,
    operador: v.operador ?? '', operadorLido: v.operador ?? '',
    preFuro: paraCampo(v.preFuroM, 2), preFuroLido: paraCampo(v.preFuroM, 2),
    observacoes: v.observacoes ?? '', observacoesLido: v.observacoes ?? '',
    substituicao: '',
  };
}

const numeroOuNulo = (t: string): number | null => (t.trim() === '' ? null : paraNumero(t) ?? NaN);

/** «CPTu02, CPTu05 e CPTu07». */
const listaDeCodigos = (cs: string[]) => (cs.length <= 1 ? cs.join('')
  : `${cs.slice(0, -1).join(', ')} e ${cs[cs.length - 1]}`);

/**
 * O «Gravado.» diz o que ESTA importação fez — e não um número que se lê como o total da campanha (agent-browser da
 * 312): «2 verticais gravadas (1 substituiu a anterior; 1 foto passou para a nova) · 2.031 leituras importadas».
 */
export function recadoDoGravado(g: Conferencia['gravado']): string {
  if (!g) return 'A importação foi gravada.';
  const subs = g.substituidas ?? [];
  const fotos = subs.reduce((n, s) => n + (s.fotos ?? 0), 0);
  const detalhes = [
    subs.length === 1 ? '1 substituiu a anterior' : subs.length > 1 ? `${subs.length} substituíram as anteriores` : null,
    fotos ? `${plural(fotos, 'foto passou', 'fotos passaram')} para ${subs.length > 1 ? 'as novas' : 'a nova'}` : null,
  ].filter(Boolean).join('; ');
  const partes = [
    g.verticais.length
      ? `${plural(g.verticais.length, 'vertical gravada', 'verticais gravadas')}${detalhes ? ` (${detalhes})` : ''}` : null,
    g.dissipacoes ? plural(g.dissipacoes, 'dissipação gravada', 'dissipações gravadas') : null,
  ].filter(Boolean).join(' e ');
  return `${partes || 'Nada novo gravado'}`
    + `${g.leituras ? ` · ${numero(g.leituras, 0)} leituras importadas` : ''}`
    + `${g.mantidas.length ? ` · ${plural(g.mantidas.length, 'dissipação idêntica mantida', 'dissipações idênticas mantidas')}` : ''}.`;
}

/** A decisão de uma vertical, com só o que o engenheiro mudou (o resto o servidor lê do arquivo). */
function decisaoDaVertical(e: EdicaoDaVertical): DecisaoDaVertical {
  const d: DecisaoDaVertical = {
    arquivo: e.arquivo,
    codigo: e.formato === 'cpeit_cpt' ? e.codigoLido : e.codigo.trim(),
    dataEnsaio: e.data,
  };
  /* O número que não se lê não vai (o JSON o mandaria como null, que APAGA o valor lido). */
  const lido = (t: string) => { const n = numeroOuNulo(t); return n === null || Number.isFinite(n) ? n : undefined; };
  if (e.na !== e.naLido && lido(e.na) !== undefined) d.nivelAgua = lido(e.na);
  if (e.cota !== e.cotaLido && lido(e.cota) !== undefined) d.cota = lido(e.cota);
  const [lat, lon] = [lido(e.lat), lido(e.lon)];
  if (textoDaCoordenada(e.lat, e.lon) !== e.coordenadaLida && lat !== undefined && lon !== undefined
    && (lat === null) === (lon === null)) {
    d.latitude = lat;
    d.longitude = lon;
  }
  if (e.preFuro !== e.preFuroLido && lido(e.preFuro) !== undefined) d.preFuroM = lido(e.preFuro);
  if (e.operador.trim() !== e.operadorLido.trim()) d.operador = e.operador.trim() || null;
  if (e.observacoes.trim() !== e.observacoesLido.trim()) d.observacoes = e.observacoes.trim() || null;
  /* O encerramento vai inteiro quando muda; «Não informado» apaga o que veio da importação anterior. */
  if (e.parada !== e.paradaLida || (e.parada === 'outro' && e.paradaTexto.trim() !== e.paradaTextoLido.trim())) {
    d.motivoParada = e.parada || null;
    if (e.parada === 'outro' && e.paradaTexto.trim()) d.motivoParadaTexto = e.paradaTexto.trim();
  }
  if (e.substituicao.trim()) d.motivoSubstituicao = e.substituicao.trim();
  if (e.cone) d.coneEquipamentoId = e.cone.id;
  return d;
}

/** O que falta para a vertical entrar — ou null. */
function faltaNaVertical(e: EdicaoDaVertical, v: ConferenciaDaVertical | undefined): string | null {
  if (!e.incluir || !v) return null;
  /* O ponto recusado (de outra família, ou de outra campanha) não entra: o motivo é o do servidor. */
  if (v.ponto.acao === 'recusar') return v.ponto.motivo ?? `O ponto ${v.ponto.codigo} não recebe vertical CPTu.`;
  if (!e.codigo.trim()) return 'Diga o código da vertical.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.data)) return 'Informe a data do ensaio.';
  const nums = [e.na, e.cota, e.lat, e.lon, e.preFuro].map(numeroOuNulo);
  if (nums.some((n) => n !== null && !Number.isFinite(n))) return 'Há um número que não se lê: use vírgula ou ponto para as casas.';
  if ((nums[2] === null) !== (nums[3] === null)) return 'A coordenada vem inteira (latitude e longitude) ou não vem.';
  if (e.parada === 'outro' && !e.paradaTexto.trim()) return 'Com o encerramento «outro motivo», escreva qual.';
  if (faltaOMotivo(e, v)) return textoDoMotivoQueFalta(e, v);
  return null;
}

/** A vertical que substitui outra (R8) e ainda não diz o motivo. */
const faltaOMotivo = (e: EdicaoDaVertical, v: ConferenciaDaVertical) => !!v.substitui && e.substituicao.trim().length < 10;
/** A frase do motivo que falta, com a vertical e a importação que ela substitui (agent-browser da 312). */
const textoDoMotivoQueFalta = (e: EdicaoDaVertical, v: ConferenciaDaVertical) =>
  `A vertical ${codigoQueEntra(e) || v.codigo} substitui a importação de ${v.substitui ? data(v.substitui.importadaEm) : ''}: `
  + 'diga o motivo (pelo menos 10 caracteres).';

/* ── O mapa de colunas do CSV ───────────────────────────────────────── */

type EscolhaDoCsv =
  | { tipo: 'modelo'; id: string }
  | { tipo: 'mapa' };

interface CanalDoMapa { chave: keyof MapaDeColunas['colunas']; rotulo: string; obrigatorio: boolean; unidades: string[] }

const CANAIS: CanalDoMapa[] = [
  { chave: 'profundidade', rotulo: 'Profundidade', obrigatorio: true, unidades: ['m', 'cm'] },
  { chave: 'qc', rotulo: 'qc', obrigatorio: true, unidades: ['MPa', 'kPa'] },
  { chave: 'fs', rotulo: 'fs', obrigatorio: true, unidades: ['kPa', 'MPa'] },
  { chave: 'u2', rotulo: 'u₂', obrigatorio: true, unidades: ['kPa', 'MPa'] },
  { chave: 'inclinacao', rotulo: 'Inclinação', obrigatorio: false, unidades: [] },
  { chave: 'velocidade', rotulo: 'Velocidade', obrigatorio: false, unidades: ['cm/s', 'mm/s'] },
  { chave: 'tempo', rotulo: 'Tempo', obrigatorio: false, unidades: [] },
];

/** O mapa à mão, campo a campo, como a tela o edita (a unidade NUNCA se presume: o engenheiro escolhe). */
interface MapaEmEdicao {
  delimitador: MapaDeColunas['delimitador'];
  decimal: MapaDeColunas['decimal'];
  linhaCabecalho: string;
  primeiraLinhaDados: string;
  colunas: { [canal: string]: { coluna: string; unidade: string } };
}

function mapaInicial(cab: CabecalhoDoCsv): MapaEmEdicao {
  const colunas: MapaEmEdicao['colunas'] = {};
  for (const c of CANAIS) colunas[c.chave] = { coluna: '', unidade: '' };
  return {
    delimitador: cab.delimitador, decimal: cab.decimal,
    linhaCabecalho: String(cab.linhaCabecalho), primeiraLinhaDados: String(cab.primeiraLinhaDados),
    colunas,
  };
}

/** O mapa em edição como a API o quer, ou o que falta. */
function mapaPronto(m: MapaEmEdicao): { mapa: MapaDeColunas | null; falta: string | null } {
  const linhaCabecalho = Number(m.linhaCabecalho);
  const primeira = Number(m.primeiraLinhaDados);
  if (!Number.isInteger(linhaCabecalho) || linhaCabecalho < 0) return { mapa: null, falta: 'A linha do cabeçalho é um número (0 = sem títulos).' };
  if (!Number.isInteger(primeira) || primeira <= linhaCabecalho) {
    return { mapa: null, falta: 'A primeira linha de dados vem depois da linha do cabeçalho.' };
  }
  if (m.delimitador === ',' && m.decimal === ',') return { mapa: null, falta: 'A vírgula não separa os campos e as casas ao mesmo tempo.' };
  const colunas: Record<string, unknown> = {};
  for (const c of CANAIS) {
    const x = m.colunas[c.chave];
    if (!x?.coluna) {
      if (c.obrigatorio) return { mapa: null, falta: `Escolha a coluna de ${c.rotulo}.` };
      continue;
    }
    if (c.unidades.length && !x.unidade) return { mapa: null, falta: `Escolha a unidade de ${c.rotulo}.` };
    const ref: ColunaDoMapa = x.coluna.startsWith('#') ? Number(x.coluna.slice(1)) : x.coluna;
    colunas[c.chave] = c.unidades.length ? { coluna: ref, unidade: x.unidade } : { coluna: ref };
  }
  return {
    mapa: {
      delimitador: m.delimitador, decimal: m.decimal, linhaCabecalho, primeiraLinhaDados: primeira,
      colunas: colunas as MapaDeColunas['colunas'],
    },
    falta: null,
  };
}

/* ── A importação ───────────────────────────────────────────────────── */

function Importacao({ c }: { c: CampanhaCompleta }) {
  const navegar = useNavigate();
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [erroDosArquivos, setErroDosArquivos] = useState<string | null>(null);
  /* O CSV: o cabeçalho do primeiro CSV, os modelos e o mapa (um mapa só vale para todos os CSV do envio). */
  const [cabecalho, setCabecalho] = useState<{ arquivo: string; dados: CabecalhoDoCsv | null; erro: unknown } | null>(null);
  const [modelos, setModelos] = useState<ModeloDeImportacao[]>([]);
  const [escolhaCsv, setEscolhaCsv] = useState<EscolhaDoCsv>({ tipo: 'mapa' });
  const [mapa, setMapa] = useState<MapaEmEdicao | null>(null);
  /* A conferência e o que o engenheiro decidiu nela. */
  const [conf, setConf] = useState<Conferencia | null>(null);
  const [confDe, setConfDe] = useState<string | null>(null);
  const [verticais, setVerticais] = useState<EdicaoDaVertical[]>([]);
  const [dissipacoes, setDissipacoes] = useState<EdicaoDaDissipacao[]>([]);
  const [aplicarProjeto, setAplicarProjeto] = useState(false);
  const [ocupado, setOcupado] = useState<'conferir' | 'gravar' | null>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  /* O decidido mais novo, para a conferência que volta juntar ao que a pessoa mexeu enquanto ela ia. */
  const decidido = useRef({ verticais, dissipacoes });
  decidido.current = { verticais, dissipacoes };

  const csvs = arquivos.filter(ehCsv);
  const primeiroCsv = csvs[0] ?? null;
  const sigs: Assinaturas = useMemo(() => new Map(arquivos.map((f) => [f.name, assinaturaDoArquivo(f)])), [arquivos]);
  const chaveDe = useCallback((v: ConferenciaDaVertical) => chaveDaVertical(v, sigs), [sigs]);

  /* O cabeçalho do primeiro CSV: propõe o separador, as linhas e os modelos que servem. */
  useEffect(() => {
    if (!primeiroCsv) { setCabecalho(null); return undefined; }
    if (cabecalho?.arquivo === primeiroCsv.name) return undefined;
    let vivo = true;
    const fd = new FormData();
    fd.append('arquivo', primeiroCsv);
    setCabecalho({ arquivo: primeiroCsv.name, dados: null, erro: null });
    lerCabecalhoDoCsv(fd).then((r) => {
      if (!vivo) return;
      setCabecalho({ arquivo: primeiroCsv.name, dados: r, erro: null });
      setMapa(mapaInicial(r));
      /* Só o modelo que casou pelos TÍTULOS vem escolhido; o que casou só pela posição a pessoa escolhe. */
      const forte = r.modelos.find((m) => m.por === 'titulo');
      setEscolhaCsv(forte ? { tipo: 'modelo', id: forte.id } : { tipo: 'mapa' });
    }).catch((e) => { if (vivo) setCabecalho({ arquivo: primeiroCsv.name, dados: null, erro: e }); });
    listarModelosDeImportacao().then((r) => { if (vivo) setModelos(r); }).catch(() => undefined);
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primeiroCsv?.name]);

  const pronto = mapa ? mapaPronto(mapa) : { mapa: null, falta: null };
  const csvDaDecisao: Decisoes['csv'] | undefined = !csvs.length ? undefined
    : escolhaCsv.tipo === 'modelo' ? { modeloId: escolhaCsv.id }
      : pronto.mapa ? { mapa: pronto.mapa } : undefined;
  const faltaNoCsv = csvs.length > 0 && !csvDaDecisao
    ? (pronto.falta ?? 'Monte o mapa de colunas do CSV.') : null;

  /*
   * A conferência vale para ESTES arquivos, ESTE mapa e ESTES códigos: mudou um deles (o código de
   * uma vertical, a vertical de uma dissipação), confira de novo — o servidor confere o ponto da OS
   * pelo código.
   */
  const assinaturaDosArquivos = useMemo(
    () => JSON.stringify([arquivos.map(assinaturaDoArquivo), csvDaDecisao ?? null]),
    [arquivos, csvDaDecisao],
  );
  const confValida = !!conf && confDe === `${assinaturaDosArquivos}§${assinaturaDasDecisoes(verticais, dissipacoes)}`;

  /** Recomeça a conferência: nada do que foi decidido sobre os arquivos de antes vale. */
  const esquecerConferencia = () => {
    setConf(null); setConfDe(null); setVerticais([]); setDissipacoes([]); setAplicarProjeto(false); setErro(null);
  };

  const juntarArquivos = (novos: File[]) => {
    setErroDosArquivos(null);
    const porNome = new Map(arquivos.map((f) => [f.name, f]));
    /* O arquivo de mesmo nome e conteúdo diferente TROCA o de antes: a conferência e o decidido caem. */
    const trocado = novos.some((f) => {
      const antes = porNome.get(f.name);
      return !!antes && assinaturaDoArquivo(antes) !== assinaturaDoArquivo(f);
    });
    for (const f of novos) porNome.set(f.name, f);
    const todos = [...porNome.values()];
    if (todos.length > ARQUIVOS_POR_IMPORTACAO) {
      setErroDosArquivos(`Uma importação leva até ${ARQUIVOS_POR_IMPORTACAO} arquivos: importe o resto depois.`);
      return;
    }
    if (somaDosTamanhos(todos) > BYTES_POR_IMPORTACAO) {
      setErroDosArquivos(RECADO_DA_IMPORTACAO_GRANDE);
      return;
    }
    if (trocado) esquecerConferencia();
    setArquivos(todos);
  };

  /**
   * O envio, conferido antes pela soma dos tamanhos; o 413 sem corpo da API (o do nginx, em HTML) sai com a
   * frase da API — e não com «Falha no envio (413)».
   */
  const enviar = async (fd: FormData, aoProgredir?: (f: number) => void) => {
    if (somaDosTamanhos(arquivos) > BYTES_POR_IMPORTACAO) throw new ErroApi(413, RECADO_DA_IMPORTACAO_GRANDE);
    try {
      return await importarArquivosCptu(c.campanha.id, fd, aoProgredir);
    } catch (e) {
      if (e instanceof ErroApi && e.status === 413 && /^Falha no envio/.test(e.message)) {
        throw new ErroApi(413, RECADO_DA_IMPORTACAO_GRANDE);
      }
      throw e;
    }
  };

  const formulario = (simular: boolean, decisoes: Decisoes | null) => {
    const fd = new FormData();
    /* Um envio só, e não lotes: a dissipação se liga à vertical no mesmo pedido (isento do 26.4). */
    for (const f of arquivos) fd.append('arquivos', f);
    if (simular) fd.append('simular', 'true');
    if (decisoes) fd.append('decisoes', JSON.stringify(decisoes));
    return fd;
  };

  /** As decisões de agora: as verticais marcadas com data, as dissipações marcadas com vertical, o CSV. */
  const decisoes = useCallback((soMarcadas: boolean): Decisoes => {
    const vs = verticais.filter((e) => (soMarcadas ? e.incluir : true) && /^\d{4}-\d{2}-\d{2}$/.test(e.data));
    return {
      verticais: vs.map(decisaoDaVertical),
      dissipacoes: dissipacoes.filter((e) => (soMarcadas ? e.incluir : true) && e.vertical)
        .map((e) => ({ arquivo: e.arquivo, profundidade: e.profundidade, vertical: e.vertical })),
      ...(csvDaDecisao ? { csv: csvDaDecisao } : {}),
      ...(conf?.premissasDoProjeto ? { aplicarPremissasDoProjeto: aplicarProjeto } : {}),
    };
  }, [verticais, dissipacoes, csvDaDecisao, conf, aplicarProjeto]);

  /**
   * Junta a conferência nova ao que já foi decidido: o decidido fica; o que é novo nasce com o padrão.
   * O que a conferência diz idêntico ao gravado, ou recusado, sai desmarcado mesmo que estivesse
   * marcado — a pessoa marca de novo, se quiser, sabendo.
   */
  const receberConferencia = (r: Conferencia, assinatura: string) => {
    const antes = decidido.current;
    const lidas = r.verticais.map((v) => {
      const chave = chaveDe(v);
      const ja = antes.verticais.find((e) => e.chave === chave);
      if (!ja) return novaEdicaoDaVertical(v, chave);
      return ehIdentica(v.avisos) || v.ponto.acao === 'recusar' ? { ...ja, incluir: false } : ja;
    });
    /* A cópia que chega depois (os mesmos bytes de um arquivo de antes, nesta importação) fica desmarcada enquanto
       o primeiro do par entra — também em «Conferir de novo»: as duas juntas o servidor recusa (agent-browser da 312). */
    const vs = lidas.map((e, i) => {
      const gemea = gemeaDe(r.verticais[i], r.verticais);
      const daGemea = gemea ? lidas[r.verticais.indexOf(gemea)] : undefined;
      return e.incluir && daGemea?.incluir ? { ...e, incluir: false } : e;
    });
    const ds = r.dissipacoes.map((d): EdicaoDaDissipacao => {
      const ja = antes.dissipacoes.find((e) => mesmaDissipacao(e, d, sigs));
      if (ja) return ehIdentica(d.avisos) ? { ...ja, incluir: false } : ja;
      const doMesmoArquivo = r.dissipacoes.filter((x) => x.arquivo === d.arquivo).length;
      return {
        arquivo: d.arquivo, assinatura: assinaturaDe(d.arquivo, sigs), profundidade: d.profundidade,
        verticalLida: d.vertical, travada: doMesmoArquivo > 1 || /\.cpt$/i.test(d.arquivo),
        incluir: !ehIdentica(d.avisos), vertical: d.vertical ?? '',
      };
    });
    setVerticais(vs);
    setDissipacoes(ds);
    setConf(r);
    setConfDe(`${assinatura}§${assinaturaDasDecisoes(vs, ds)}`);
  };

  const conferir = async (comDecisoes: boolean) => {
    setOcupado('conferir'); setErro(null);
    try {
      const d = comDecisoes ? decisoes(false) : (csvDaDecisao ? { verticais: [], dissipacoes: [], csv: csvDaDecisao } : null);
      const assinatura = assinaturaDosArquivos;
      receberConferencia(await enviar(formulario(true, d)), assinatura);
    } catch (e) { setErro(e); } finally { setOcupado(null); }
  };

  const vertDaConf = (e: EdicaoDaVertical) => conf?.verticais.find((v) => chaveDe(v) === e.chave);
  const marcadas = verticais.filter((e) => e.incluir);
  const dissMarcadas = dissipacoes.filter((e) => e.incluir);
  /* O servidor acha a vertical da dissipação sem olhar maiúscula e minúscula (CPTU01 × CPTu01). */
  const codigosQueEntram = new Set([...marcadas.map(codigoQueEntra), ...c.verticais.map((v) => v.codigo)]
    .map((x) => x.toLowerCase()));
  /* Um arquivo por vertical: o mesmo código marcado em dois arquivos não entra (o servidor recusaria). */
  const repetidos = [...marcadas.reduce((m, e) => {
    const k = codigoQueEntra(e).toLowerCase();
    if (k) m.set(k, [...(m.get(k) ?? []), e]);
    return m;
  }, new Map<string, EdicaoDaVertical[]>()).values()].filter((l) => l.length > 1);
  /*
   * O que o servidor recusaria (409) trava o «Importar», com o motivo: o arquivo já gravado marcado de novo
   * («desmarque-o», rodada final da 312) e as duas cópias do mesmo arquivo marcadas juntas («desmarque um dos dois»).
   * Os avisos dos dois casos contam à parte na barra — «nenhum bloqueia» só quando é verdade (agent-browser da 312).
   */
  const marcadasNaConf = marcadas.map((e) => ({ e, v: vertDaConf(e) }))
    .filter((x): x is { e: EdicaoDaVertical; v: ConferenciaDaVertical } => !!x.v);
  const paresMarcados = conf ? marcadasNaConf.flatMap(({ e, v }) => {
    const gemea = gemeaDe(v, conf.verticais);
    const daGemea = gemea ? verticais.find((x) => x.chave === chaveDe(gemea)) : undefined;
    return daGemea?.incluir ? [{ e, gemea: daGemea }] : [];
  }) : [];
  const jaGravadas = new Set(marcadasNaConf.filter(({ v }) => ehIdentica(v.avisos)).map(({ e }) => e));
  const bloqueios = [
    ...[...jaGravadas].map((e) => `${e.arquivo} já está gravado: desmarque-o.`),
    ...paresMarcados.map(({ e, gemea }) => `${gemea.arquivo} e ${e.arquivo} têm os mesmos bytes (o mesmo arquivo com `
      + 'outro nome): desmarque um dos dois — as leituras entrariam duas vezes.'),
  ];
  const avisosQueBloqueiam = marcadasNaConf.reduce((n, { e, v }) => n + v.avisos.filter((a) => a.tipo === 'atencao'
    && ((/O mesmo arquivo já está gravado/.test(a.texto) && jaGravadas.has(e))
      || (/tem os mesmos bytes de/.test(a.texto) && paresMarcados.some((x) => x.e === e || x.gemea === e)))).length, 0);
  /* O motivo da substituição que falta, dito com as verticais: uma frase só quando são várias. */
  const semMotivo = marcadasNaConf.filter(({ e, v }) => faltaNaVertical(e, v) === textoDoMotivoQueFalta(e, v));
  const faltaDeMotivo = semMotivo.length > 1
    ? `As verticais ${listaDeCodigos(semMotivo.map(({ e, v }) => codigoQueEntra(e) || v.codigo))} substituem `
      + 'importações: diga o motivo de cada uma (pelo menos 10 caracteres).'
    : null;
  const faltas = [
    ...bloqueios,
    ...marcadasNaConf.map(({ e, v }) => faltaNaVertical(e, v))
      .filter((x): x is string => !!x && !(faltaDeMotivo && semMotivo.some(({ e, v }) => x === textoDoMotivoQueFalta(e, v)))),
    ...(faltaDeMotivo ? [faltaDeMotivo] : []),
    ...repetidos.map((l) => `A vertical ${codigoQueEntra(l[0])} está marcada em ${l.length} arquivos `
      + `(${l.map((e) => e.arquivo).join(', ')}): só um arquivo por vertical entra.`),
    ...dissMarcadas.filter((e) => !e.vertical || !codigosQueEntram.has(e.vertical.toLowerCase()))
      .map((e) => `A dissipação de ${e.arquivo} vai para uma vertical que não entra nesta importação nem está na campanha.`),
  ];
  /* O CSV sem mapa não trava o resto: ele fica entre os recusados, e os outros arquivos entram. */
  const faltaParaImportar = !conf ? 'Confira antes de importar.'
    : !confValida ? 'Os arquivos, o mapa ou os códigos mudaram: confira de novo.'
      : marcadas.length + dissMarcadas.length === 0 ? 'Marque o que vai entrar.'
        : faltas[0] ?? null;

  const gravar = async () => {
    setOcupado('gravar'); setErro(null); setProgresso(0);
    try {
      const r = await enviar(formulario(false, decisoes(true)), setProgresso);
      navegar(`/cptu/${c.campanha.id}`, { state: { gravado: recadoDoGravado(r.gravado) } });
    } catch (e) { setErro(e); setOcupado(null); setProgresso(null); }
  };

  /* A recusa da gravação item por item (`{ mensagem, recusas }`, 400) o `ErroDaApi` lista no próprio aviso. */
  const conflito = erro instanceof ErroApi && (erro.status === 409 || erro.status === 400) && !!conf;
  const mensagemDoErro = erro instanceof Error ? erro.message : '';
  /* Os avisos dos cartões MARCADOS (o desmarcado não entra, e os avisos dele não contam). */
  const atencoes = [
    ...marcadasNaConf.map(({ v }) => v.avisos),
    ...(conf ? conf.dissipacoes.flatMap((d) => (dissMarcadas.some((e) => mesmaDissipacao(e, d, sigs)) ? [d.avisos] : [])) : []),
  ].reduce((n, avisos) => n + avisos.filter((a) => a.tipo === 'atencao').length, 0);

  return (
    <>
      <p className="explicacao cptu-importar-explica">
        Arraste os arquivos do Pagani (.xlsx de vertical e de dissipação), um CSV/TXT de outro equipamento ou
        o projeto do CPeT-IT (.cpt). O sistema reconhece cada arquivo pelo conteúdo, liga a vertical ao ponto
        da OS e confere antes de gravar. O arquivo original fica guardado como veio, com o hash.
      </p>

      <section className="cartao">
        <ZonaDeArquivo rotulo="Arquivos do equipamento" aceita={ACEITA} multiplo
          tipos="Pagani .xlsx, dissipação, CSV/TXT ou projeto do CPeT-IT"
          textoVazio="Solte os arquivos aqui" desabilitado={!!ocupado}
          arquivos={arquivos} erro={erroDosArquivos ?? undefined}
          aoEscolher={juntarArquivos} />
        {arquivos.length > 0 && (
          <div className="cptu-arquivos-escolhidos">
            <span className="sub">{plural(arquivos.length, 'arquivo', 'arquivos')} para conferir</span>
            <button type="button" className="ligacao" disabled={!!ocupado}
              onClick={() => { setArquivos([]); esquecerConferencia(); }}>
              Tirar todos
            </button>
          </div>
        )}
      </section>

      {csvs.length > 0 && (
        <MapaDoCsv cabecalho={cabecalho} csvs={csvs.length} modelos={modelos} escolha={escolhaCsv}
          aoEscolher={setEscolhaCsv} mapa={mapa} aoMudarMapa={setMapa} falta={faltaNoCsv}
          mapaPronto={pronto.mapa}
          aoGravarModelo={(m) => { setModelos((l) => [...l, m]); setEscolhaCsv({ tipo: 'modelo', id: m.id }); }} />
      )}

      {conf && <ConferenciaNaTela conf={conf} c={c} verticais={verticais} dissipacoes={dissipacoes}
        chaveDe={chaveDe} sigs={sigs} travado={!!ocupado}
        aoMudarVertical={(chave, mudar) => setVerticais((l) => l.map((e) => (e.chave === chave ? { ...e, ...mudar } : e)))}
        aoMudarDissipacao={(i, mudar) => setDissipacoes((l) => l.map((e, j) => (j === i ? { ...e, ...mudar } : e)))}
        codigosQueEntram={codigosQueEntram}
        aplicarProjeto={aplicarProjeto} aoAplicarProjeto={setAplicarProjeto} />}

      <div className="cptu-importar-acoes" role="region" aria-label="Conferir e importar">
        <div className="cptu-importar-estado">
          {erro ? <ErroDaApi erro={erro} /> : null}
          {conflito && (
            <p className="sub">
              {/os mesmos bytes de .*nesta importação/.test(mensagemDoErro)
                ? 'A gravação foi recusada: os dois arquivos com os mesmos bytes estavam marcados. Desmarque um dos dois '
                  + '(«Conferir de novo» já traz a cópia desmarcada) e importe.'
                : /O mesmo arquivo já está gravado/.test(mensagemDoErro)
                  ? 'A gravação foi recusada: o arquivo já está gravado. Desmarque-o e importe o resto.'
                  : 'A gravação foi recusada: o que está gravado mudou, ou a decisão não fecha com o arquivo. Confira de '
                    + 'novo — o que você decidiu fica.'}
            </p>
          )}
          {ocupado === 'gravar' && progresso !== null && (
            <BarraDeAvanco pct={progresso * 100} rotulo="Enviando os arquivos" />
          )}
          {!erro && conf && confValida && (
            <p className="sub">
              {plural(marcadas.length, 'vertical', 'verticais')} e{' '}
              {plural(dissMarcadas.length, 'dissipação', 'dissipações')} marcadas
              {atencoes > 0 && ` · ${plural(atencoes, 'aviso de atenção', 'avisos de atenção')} `
                + `(${avisosQueBloqueiam === 0 ? 'nenhum bloqueia'
                  : avisosQueBloqueiam === 1 ? '1 bloqueia a gravação' : `${avisosQueBloqueiam} bloqueiam a gravação`})`}
            </p>
          )}
          {faltaParaImportar && conf && <p className="cptu-falta" role="status">{faltaParaImportar}</p>}
          {!faltaParaImportar && faltaNoCsv && (
            <p className="sub">O CSV fica de fora enquanto o mapa de colunas não estiver montado.</p>
          )}
        </div>
        <Link className="botao secundario" to={`/cptu/${c.campanha.id}`}>Cancelar</Link>
        <button type="button" className="botao secundario" disabled={!arquivos.length || !!ocupado}
          onClick={() => { void conferir(!!conf); }}>
          {ocupado === 'conferir' ? 'Conferindo…' : conf ? 'Conferir de novo' : 'Conferir sem gravar'}
        </button>
        <button type="button" className="botao" disabled={!!faltaParaImportar || !!ocupado}
          onClick={() => { void gravar(); }}>
          {ocupado === 'gravar' ? 'Importando…' : 'Importar'}
        </button>
      </div>
    </>
  );
}

/* ── O mapa de colunas na tela ─────────────────────────────────────── */

function MapaDoCsv({
  cabecalho, csvs, modelos, escolha, aoEscolher, mapa, aoMudarMapa, falta, mapaPronto: pronto, aoGravarModelo,
}: {
  cabecalho: { arquivo: string; dados: CabecalhoDoCsv | null; erro: unknown } | null;
  csvs: number;
  modelos: ModeloDeImportacao[];
  escolha: EscolhaDoCsv;
  aoEscolher(e: EscolhaDoCsv): void;
  mapa: MapaEmEdicao | null;
  aoMudarMapa(m: MapaEmEdicao): void;
  falta: string | null;
  mapaPronto: MapaDeColunas | null;
  aoGravarModelo(m: ModeloDeImportacao): void;
}) {
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [gravando, setGravando] = useState(false);
  const cab = cabecalho?.dados ?? null;
  const propostos = cab?.modelos ?? [];
  const outros = modelos.filter((m) => !propostos.some((p) => p.id === m.id));
  const escolhido = escolha.tipo === 'modelo'
    ? propostos.find((m) => m.id === escolha.id) ?? modelos.find((m) => m.id === escolha.id) : null;
  const fraco = escolhido && 'por' in escolhido && escolhido.por === 'posicao';

  /* As colunas que o seletor oferece: o título (quando há) ou a posição. */
  const larguras = Math.max(cab?.colunas.length ?? 0, ...(cab?.amostra ?? []).map((l) => l.length));
  const opcoesDeColuna = Array.from({ length: larguras }, (_, i) => {
    const titulo = cab?.colunas[i]?.trim();
    const repetido = titulo ? (cab?.colunas.filter((t) => t.trim() === titulo).length ?? 0) > 1 : true;
    return titulo && !repetido && Number(mapa?.linhaCabecalho) > 0
      ? { valor: titulo, texto: `${i + 1} · ${titulo}` }
      : { valor: `#${i + 1}`, texto: `Coluna ${i + 1}${titulo ? ` · ${titulo}` : ''}` };
  });

  const gravarModelo = async () => {
    if (!pronto || !nome.trim()) return;
    setGravando(true); setErro(null);
    try { aoGravarModelo(await criarModeloDeImportacao({ nome: nome.trim(), ...pronto })); setNome(''); }
    catch (e) { setErro(e); } finally { setGravando(false); }
  };

  const mudarColuna = (canal: string, campo: 'coluna' | 'unidade', valor: string) => {
    if (!mapa) return;
    aoMudarMapa({ ...mapa, colunas: { ...mapa.colunas, [canal]: { ...mapa.colunas[canal], [campo]: valor } } });
  };

  return (
    <section className="cartao cptu-mapa">
      <div className="cartao-cabeca">
        <h2>Mapa de colunas do CSV</h2>
        <span className="sub cresce">
          {csvs > 1 ? `o mesmo mapa vale para os ${csvs} CSV desta importação` : cabecalho?.arquivo}
        </span>
      </div>
      {!cab && !cabecalho?.erro && <Carregando texto="Lendo o cabeçalho do CSV" />}
      <ErroDaApi erro={cabecalho?.erro ?? null} />
      {cab && (
        <>
          <fieldset className="cptu-modelos">
            <legend>Como ler o arquivo</legend>
            {propostos.map((m) => (
              <label key={m.id} className="campo em-linha">
                <input type="radio" name="modelo-csv" checked={escolha.tipo === 'modelo' && escolha.id === m.id}
                  onChange={() => aoEscolher({ tipo: 'modelo', id: m.id })} />
                <span>
                  Modelo «{m.nome}»{' '}
                  {m.por === 'titulo'
                    ? <span className="sub">casou pelos títulos das colunas</span>
                    : <span className="cptu-fraco"><span aria-hidden="true">▲</span> casou só pela posição das colunas — confira</span>}
                </span>
              </label>
            ))}
            {outros.length > 0 && (
              <label className="campo">
                <span className="rotulo">Outro modelo gravado</span>
                <select value={escolha.tipo === 'modelo' && outros.some((m) => m.id === escolha.id) ? escolha.id : ''}
                  onChange={(e) => (e.target.value ? aoEscolher({ tipo: 'modelo', id: e.target.value }) : undefined)}>
                  <option value="">Escolha…</option>
                  {outros.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </select>
                <span className="dica">Um modelo que não casou com este arquivo pode recusar as linhas: confira na conferência.</span>
              </label>
            )}
            <label className="campo em-linha">
              <input type="radio" name="modelo-csv" checked={escolha.tipo === 'mapa'}
                onChange={() => aoEscolher({ tipo: 'mapa' })} />
              <span>Montar o mapa à mão</span>
            </label>
          </fieldset>
          {fraco && (
            <Aviso tipo="atencao" titulo="O modelo casou só pela posição das colunas — confira.">
              O arquivo não tem os títulos que o modelo nomeia: as colunas foram achadas pela posição. Confira na
              amostra abaixo se cada canal está na coluna certa.
            </Aviso>
          )}

          <div className="rola">
            <table aria-label="As primeiras linhas do arquivo">
              <thead>
                <tr>{Array.from({ length: larguras }, (_, i) => (
                  <th key={i} scope="col" className="cptu-celula-crua">{i + 1}{cab.colunas[i] ? ` · ${cab.colunas[i]}` : ''}</th>
                ))}</tr>
              </thead>
              <tbody>
                {cab.amostra.map((l, i) => (
                  <tr key={i}>{Array.from({ length: larguras }, (_, j) => (
                    <td key={j} className="mono cptu-celula-crua">{l[j] ?? ''}</td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
          </div>

          {escolha.tipo === 'mapa' && mapa && (
            <div className="cptu-mapa-campos">
              <label className="campo">
                <span className="rotulo">Separador</span>
                <select value={mapa.delimitador}
                  onChange={(e) => aoMudarMapa({ ...mapa, delimitador: e.target.value as MapaEmEdicao['delimitador'] })}>
                  <option value=";">ponto e vírgula ( ; )</option>
                  <option value=",">vírgula ( , )</option>
                  <option value="tab">tabulação</option>
                  <option value="|">barra ( | )</option>
                </select>
              </label>
              <label className="campo">
                <span className="rotulo">Casas decimais</span>
                <select value={mapa.decimal}
                  onChange={(e) => aoMudarMapa({ ...mapa, decimal: e.target.value as MapaEmEdicao['decimal'] })}>
                  <option value=",">vírgula (1,25)</option>
                  <option value=".">ponto (1.25)</option>
                </select>
              </label>
              <CampoNumerico inteiro rotulo="Linha do cabeçalho" value={mapa.linhaCabecalho}
                dica="0 = sem títulos" onChange={(e) => aoMudarMapa({ ...mapa, linhaCabecalho: e.target.value })} />
              <CampoNumerico inteiro rotulo="Primeira linha de dados" value={mapa.primeiraLinhaDados}
                onChange={(e) => aoMudarMapa({ ...mapa, primeiraLinhaDados: e.target.value })} />
              {CANAIS.map((canal) => (
                <div key={canal.chave} className="cptu-mapa-canal">
                  <label className="campo">
                    <span className="rotulo">{canal.rotulo}{canal.obrigatorio ? '' : ' (opcional)'}</span>
                    <select value={mapa.colunas[canal.chave]?.coluna ?? ''}
                      onChange={(e) => mudarColuna(canal.chave, 'coluna', e.target.value)}>
                      <option value="">{canal.obrigatorio ? 'Escolha a coluna…' : 'Não tem'}</option>
                      {opcoesDeColuna.map((o) => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
                    </select>
                  </label>
                  {canal.unidades.length > 0 && (
                    <label className="campo">
                      <span className="rotulo">Unidade de {canal.rotulo}</span>
                      <select value={mapa.colunas[canal.chave]?.unidade ?? ''}
                        onChange={(e) => mudarColuna(canal.chave, 'unidade', e.target.value)}>
                        <option value="">Escolha…</option>
                        {canal.unidades.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </label>
                  )}
                </div>
              ))}
            </div>
          )}

          {escolha.tipo === 'mapa' && (
            <div className="cptu-mapa-modelo">
              <ErroDaApi erro={erro} />
              <Campo rotulo="Gravar como modelo" value={nome} maxLength={120} placeholder="Nome do modelo"
                dica="O próximo arquivo do mesmo equipamento reaplica o mapa sozinho."
                onChange={(e) => setNome(e.target.value)} />
              <button type="button" className="botao secundario" disabled={!pronto || !nome.trim() || gravando}
                onClick={() => { void gravarModelo(); }}>
                {gravando ? 'Gravando…' : 'Gravar o modelo'}
              </button>
            </div>
          )}
          {falta && <p className="cptu-falta" role="status">{falta}</p>}
        </>
      )}
    </section>
  );
}

/* ── A conferência na tela ─────────────────────────────────────────── */

const NOME_DO_FORMATO: { [formato: string]: string } = {
  pagani_xlsx: 'Pagani · vertical', pagani_dissipacao_xlsx: 'Pagani · dissipação',
  csv: 'CSV/TXT', cpeit_cpt: 'Projeto do CPeT-IT',
};

function ConferenciaNaTela({
  conf, c, verticais, dissipacoes, chaveDe, sigs, travado, aoMudarVertical, aoMudarDissipacao, codigosQueEntram,
  aplicarProjeto, aoAplicarProjeto,
}: {
  conf: Conferencia;
  c: CampanhaCompleta;
  verticais: EdicaoDaVertical[];
  dissipacoes: EdicaoDaDissipacao[];
  chaveDe(v: ConferenciaDaVertical): string;
  sigs: Assinaturas;
  /**
   * Um pedido em voo (conferir ou gravar): os códigos e as verticais das dissipações não mudam —
   * a resposta seria de um código que já não é o da tela, e a assinatura a daria por válida.
   */
  travado: boolean;
  aoMudarVertical(chave: string, mudar: Partial<EdicaoDaVertical>): void;
  aoMudarDissipacao(i: number, mudar: Partial<EdicaoDaDissipacao>): void;
  codigosQueEntram: Set<string>;
  aplicarProjeto: boolean;
  aoAplicarProjeto(v: boolean): void;
}) {
  const verticaisDaCampanha = c.verticais.map((v) => v.codigo);
  const opcoesDeVertical = [...new Set([...verticais.filter((e) => e.incluir).map(codigoQueEntra), ...verticaisDaCampanha])]
    .filter(Boolean).sort();
  return (
    <div className="cptu-conferencia">
      <h2 className="cptu-conferencia-titulo">A conferência</h2>
      <ResumoDosArquivos conf={conf} />

      {conf.recusados.length > 0 && (
        <Aviso tipo="erro" titulo={plural(conf.recusados.length, 'arquivo recusado', 'arquivos recusados')}>
          <ul>{conf.recusados.map((r) => <li key={r.arquivo}><b className="mono">{r.arquivo}</b>: {r.motivo}</li>)}</ul>
        </Aviso>
      )}

      {conf.premissasDoProjeto && (
        <section className="cartao">
          <label className="campo em-linha">
            <input type="checkbox" checked={aplicarProjeto} onChange={(e) => aoAplicarProjeto(e.target.checked)} />
            <span>
              Aplicar à campanha as premissas do projeto do CPeT-IT{' '}
              <span className="sub cptu-sub-da-caixa">
                {plural(Object.keys(conf.premissasDoProjeto).length, 'premissa', 'premissas')} do projeto (Nkt, γ, u₀…).
                Sem marcar, a campanha fica com as dela, e a conferência diz o que difere.
              </span>
            </span>
          </label>
        </section>
      )}

      {conf.verticais.map((v) => {
        const e = verticais.find((x) => x.chave === chaveDe(v));
        return e ? <CartaoDaVertical key={e.chave} v={v} e={e} travado={travado} aoMudar={(m) => aoMudarVertical(e.chave, m)} /> : null;
      })}

      {conf.dissipacoes.length > 0 && (
        <section className="cartao">
          <div className="cartao-cabeca">
            <h2>Dissipações</h2>
            <span className="sub cresce">{plural(conf.dissipacoes.length, 'dissipação', 'dissipações')} nos arquivos</span>
          </div>
          <div className="rola">
            <table aria-label="Dissipações da importação">
              <thead>
                <tr>
                  <th scope="col">Importar</th>
                  <th scope="col">Arquivo</th>
                  <th scope="col">Vertical</th>
                  <th scope="col" className="n">Profundidade</th>
                  <th scope="col" className="n">Pares</th>
                  <th scope="col" className="n">Até</th>
                  <th scope="col">Conferência</th>
                </tr>
              </thead>
              <tbody>
                {conf.dissipacoes.map((d) => {
                  const i = dissipacoes.findIndex((e) => mesmaDissipacao(e, d, sigs));
                  const e = dissipacoes[i];
                  if (!e) return null;
                  const semVertical = e.incluir && (!e.vertical || !codigosQueEntram.has(e.vertical.toLowerCase()));
                  return (
                    <tr key={`${d.arquivo}|${d.profundidade}|${i}`} data-dissipacao={d.arquivo}>
                      <td>
                        <label className="em-linha">
                          <input type="checkbox" checked={e.incluir}
                            onChange={(x) => aoMudarDissipacao(i, { incluir: x.target.checked })}
                            aria-label={`Importar a dissipação de ${d.arquivo}`} />
                        </label>
                      </td>
                      <th scope="row"><span className="mono">{d.arquivo}</span></th>
                      <td>
                        {e.travada ? <span className="mono">{e.vertical || '—'}</span> : (
                          <label className="campo cptu-campo-curto">
                            <span className="so-leitor">Vertical da dissipação de {d.arquivo}</span>
                            <select value={e.vertical} disabled={travado}
                              onChange={(x) => aoMudarDissipacao(i, { vertical: x.target.value })}>
                              <option value="">Escolha…</option>
                              {[...new Set([...opcoesDeVertical, ...(e.vertical ? [e.vertical] : [])])].map((o) => (
                                <option key={o} value={o}>{o}{verticaisDaCampanha.includes(o) ? ' (na campanha)' : ''}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        {semVertical && <span className="cptu-falta">esta vertical não entra</span>}
                      </td>
                      <td className="n mono">{numero(d.profundidade, 2)} m</td>
                      <td className="n mono">{numero(d.pares, 0)}</td>
                      <td className="n mono">{numero(d.tMax, 0)} s</td>
                      <td><Avisos avisos={d.avisos} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

/** Um arquivo por linha, como a prancheta: reconhecido como, a vertical, as leituras e a conta dos avisos. */
function ResumoDosArquivos({ conf }: { conf: Conferencia }) {
  type Linha = { chave: string; arquivo: string; como: string; onde: string; leituras: string; faixa: string; avisos: AvisoDaConferencia[] | null };
  const linhas: Linha[] = [
    ...conf.verticais.map((v) => ({
      chave: `v|${v.arquivo}|${v.codigo}`, arquivo: v.arquivo, como: NOME_DO_FORMATO[v.formato] ?? v.formato,
      onde: `${v.codigo} · ${v.ponto.acao === 'usar' ? 'ponto existente' : v.ponto.acao === 'criar' ? 'ponto novo na OS' : 'ponto recusado'}`,
      leituras: numero(v.leituras, 0),
      faixa: v.profundidadeInicial !== null && v.profundidadeFinal !== null
        ? `${faixaProfundidade(v.profundidadeInicial, v.profundidadeFinal)} m` : '—',
      avisos: v.avisos,
    })),
    ...conf.dissipacoes.map((d) => ({
      chave: `d|${d.arquivo}|${d.profundidade}|${d.vertical}`, arquivo: d.arquivo,
      como: /\.cpt$/i.test(d.arquivo) ? 'Projeto do CPeT-IT · dissipação' : 'Dissipação',
      onde: `${d.vertical ?? '—'} · ${numero(d.profundidade, 2)} m`, leituras: numero(d.pares, 0),
      faixa: `t até ${numero(d.tMax, 0)} s`, avisos: d.avisos,
    })),
    ...conf.recusados.map((r) => ({
      chave: `r|${r.arquivo}`, arquivo: r.arquivo, como: '—', onde: '—', leituras: '—', faixa: '—', avisos: null,
    })),
  ];
  return (
    <section className="cartao">
      <div className="rola">
        <table aria-label="Os arquivos conferidos">
          <thead>
            <tr>
              <th scope="col">Arquivo</th>
              <th scope="col">Reconhecido como</th>
              <th scope="col">Vertical / ponto da OS</th>
              <th scope="col" className="n">Leituras</th>
              <th scope="col" className="n">Profundidade</th>
              <th scope="col">Conferência</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const n = l.avisos?.filter((a) => a.tipo === 'atencao').length ?? 0;
              return (
                <tr key={l.chave}>
                  <th scope="row"><span className="mono">{l.arquivo}</span></th>
                  <td>{l.como}</td>
                  <td>{l.onde}</td>
                  <td className="n mono">{l.leituras}</td>
                  <td className="n mono">{l.faixa}</td>
                  <td>
                    {l.avisos === null ? <SeloDaEscada degrau={RESULTADO_DA_CONFERENCIA_CPTU.recusado} />
                      : n > 0 ? <SeloDaEscada degrau={RESULTADO_DA_CONFERENCIA_CPTU.com_avisos} texto={plural(n, 'aviso', 'avisos')} />
                        : <SeloDaEscada degrau={RESULTADO_DA_CONFERENCIA_CPTU.ok} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Os avisos: a atenção com o símbolo e a palavra; a informação, mais leve. */
function Avisos({ avisos }: { avisos: AvisoDaConferencia[] }) {
  if (!avisos.length) return <span className="sub">sem aviso</span>;
  return (
    <ul className="cptu-avisos">
      {avisos.map((a, i) => (
        <li key={i} className={a.tipo}>
          <span className="cptu-aviso-simbolo" aria-hidden="true">{a.tipo === 'atencao' ? '▲' : 'i'}</span>
          <span>
            {a.tipo === 'atencao' ? <b>Atenção: </b> : <span className="so-leitor">Informação: </span>}
            {a.texto}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** O cartão de uma vertical: o que o arquivo trouxe, o que o sistema notou e o que o engenheiro decide. */
function CartaoDaVertical({ v, e, travado, aoMudar }: {
  v: ConferenciaDaVertical; e: EdicaoDaVertical; travado: boolean; aoMudar(m: Partial<EdicaoDaVertical>): void;
}) {
  const recusada = v.ponto.acao === 'recusar';
  const falta = faltaNaVertical(e, v);
  const cpt = e.formato === 'cpeit_cpt';
  const datas = [v.data.iso, v.data.alternativa].filter((x): x is string => !!x);
  const coneTrocado = e.cone !== undefined && e.cone.id !== v.cone.equipamentoId;
  /* O que veio da vertical substituída (o arquivo não traz): a dica diz, enquanto a pessoa não muda. */
  const daAnterior = new Set(v.daAnterior ?? []);
  const DA_ANTERIOR = 'Da importação anterior (o arquivo não traz).';
  const anterior = (campo: CampoDaAnterior, intocado: boolean) => (daAnterior.has(campo) && intocado ? DA_ANTERIOR : undefined);
  /* Sem número no arquivo e sem cone escolhido não há o que estar «fora de Equipamentos»: o aviso pede o cone. */
  const seloDoCone = !v.cone.codigo && !v.cone.equipamentoId ? null : (
    <SeloDaEscada degrau={CONE_NA_DATA[!v.cone.equipamentoId ? 'fora_do_parque' : v.cone.impedimento ? 'impedido' : 'apto']} />
  );
  /*
   * O cartão desmarcado porque o arquivo já está gravado, ou porque é a cópia de outro desta importação, não pede
   * data, NA nem motivo: ele mostra só o porquê (agent-browser da 312). Marcado de novo, a conferência volta inteira.
   */
  const deFora = !e.incluir && (ehIdentica(v.avisos) || !!v.copiaDe);
  const avisosDoCartao = deFora ? v.avisos.filter(ehAvisoDoMesmoArquivo) : v.avisos;
  /* O texto do encerramento que o arquivo trouxe sem o motivo da lista (o .cpt): à vista, porque é o que se grava. */
  const textoDoEncerramento = e.paradaTextoLido.trim();
  return (
    <section className={`cartao cptu-vertical${e.incluir ? '' : ' fora'}`} data-vertical={e.chave}>
      <div className="cptu-vertical-cabeca">
        <label className="em-linha cptu-incluir">
          <input type="checkbox" checked={e.incluir && !recusada} disabled={recusada}
            onChange={(x) => aoMudar({ incluir: x.target.checked })}
            aria-label={`Importar a vertical ${v.codigo}`} />
          <span>Importar</span>
        </label>
        <h3 className="mono">{v.codigo}</h3>
        <span className="cptu-ponto">
          Ponto da OS:{' '}
          <b>{v.ponto.acao === 'usar' ? `usar ${v.ponto.codigo}` : v.ponto.acao === 'criar' ? `criar ${v.ponto.codigo}` : 'recusado'}</b>
        </span>
        <span className="sub cresce">{NOME_DO_FORMATO[v.formato] ?? v.formato} · <span className="mono">{v.arquivo}</span></span>
      </div>

      <p className="cptu-leituras">
        {numero(v.leituras, 0)} {v.leituras === 1 ? 'leitura' : 'leituras'}
        {v.profundidadeInicial !== null && v.profundidadeFinal !== null
          && ` · ${faixaProfundidade(v.profundidadeInicial, v.profundidadeFinal)} m`}
        {v.passoCm !== null && ` · passo de ${numero(v.passoCm, 2, true)} cm`}
        {v.velocidade.media !== null && ` · velocidade média ${numero(v.velocidade.media, 1)} mm/s`}
      </p>

      {e.incluir && (
        <div className="cptu-campos">
          {!cpt && (
            <Campo rotulo="Código da vertical" value={e.codigo} maxLength={30} disabled={travado}
              dica={travado ? 'Esperando a resposta do servidor.' : undefined}
              onChange={(x) => aoMudar({ codigo: x.target.value })} />
          )}
          {datas.length > 1 ? (
            <fieldset className="cptu-data">
              <legend>Data do ensaio</legend>
              {datas.map((d, i) => (
                <label key={d} className="em-linha">
                  <input type="radio" name={`data-${e.chave}`} checked={e.data === d}
                    onChange={() => aoMudar({ data: d })} />
                  <span>{dataDoDia(d)}{i === 0 ? ' (como foi lida)' : ' (a outra leitura)'}</span>
                </label>
              ))}
            </fieldset>
          ) : (
            <Campo rotulo="Data do ensaio" type="date" value={e.data} required
              dica={v.data.iso ? `Lida: ${dataDoDia(v.data.iso)}`
                : daAnterior.has('data') && v.dataEnsaio && e.data === v.dataEnsaio
                  ? `Da importação anterior: ${dataDoDia(v.dataEnsaio)} (o arquivo não traz a data).`
                  : 'O arquivo não traz a data: informe-a.'}
              onChange={(x) => aoMudar({ data: x.target.value })} />
          )}
          <CampoNumerico rotulo="NA (m)" value={e.na}
            dica={anterior('na', e.na === e.naLido)
              ?? (e.na.trim() ? 'A profundidade do nível d\'água.' : 'Sem o NA, o motor calcula com a água na superfície.')}
            onChange={(x) => aoMudar({ na: x.target.value })} />
          <CampoNumerico rotulo="Cota (m)" value={e.cota} dica={anterior('cota', e.cota === e.cotaLido)}
            onChange={(x) => aoMudar({ cota: x.target.value })} />
          <CampoNumerico rotulo="Latitude" value={e.lat}
            dica={anterior('coordenada', textoDaCoordenada(e.lat, e.lon) === e.coordenadaLida)}
            onChange={(x) => aoMudar({ lat: x.target.value })} />
          <CampoNumerico rotulo="Longitude" value={e.lon} onChange={(x) => aoMudar({ lon: x.target.value })} />
          <CampoNumerico rotulo="Pré-furo (m)" value={e.preFuro} dica={anterior('preFuro', e.preFuro === e.preFuroLido)}
            onChange={(x) => aoMudar({ preFuro: x.target.value })} />
          <label className="campo">
            <span className="rotulo">Encerramento</span>
            <select value={e.parada} onChange={(x) => aoMudar({ parada: x.target.value as MotivoDeParada | '' })}>
              <option value="">{textoDoEncerramento ? 'Sem motivo da lista' : 'Não informado'}</option>
              {MOTIVOS_DE_PARADA.map(([m, t]) => <option key={m} value={m}>{t}</option>)}
            </select>
            {anterior('encerramento', e.parada === e.paradaLida && e.paradaTexto === e.paradaTextoLido)
              && <span className="dica">{DA_ANTERIOR}</span>}
            {/* Com «outro motivo», o texto vai no «Qual motivo», já preenchido com ele. */}
            {e.parada !== 'outro' && textoDoEncerramento && (
              <span className="dica cptu-encerramento-lido">
                {daAnterior.has('encerramento') ? 'Texto do encerramento' : 'Lido do arquivo'}: «{textoDoEncerramento}».
              </span>
            )}
          </label>
          {e.parada === 'outro' && (
            <Campo rotulo="Qual motivo" value={e.paradaTexto} maxLength={500}
              onChange={(x) => aoMudar({ paradaTexto: x.target.value })} />
          )}
          <Campo rotulo="Operador" value={e.operador} maxLength={200} dica={anterior('operador', e.operador === e.operadorLido)}
            onChange={(x) => aoMudar({ operador: x.target.value })} />
          <Campo rotulo="Observações" value={e.observacoes} maxLength={4000}
            dica={anterior('observacoes', e.observacoes === e.observacoesLido)}
            onChange={(x) => aoMudar({ observacoes: x.target.value })} />
          <div className="cptu-cone-da-vertical">
            <span className="rotulo">Cone {v.cone.codigo ? <span className="mono">{v.cone.codigo}</span> : 'não informado no arquivo'}</span>
            {!coneTrocado && seloDoCone}
            {v.cone.impedimento && !coneTrocado && <span className="sub">{v.cone.impedimento}</span>}
            <EscolhaDoCone valor={e.cone ? e.cone.id : v.cone.equipamentoId}
              rotuloAtual={e.cone ? e.cone.rotulo : v.cone.equipamentoId
                ? (v.cone.codigo ? `nº ${v.cone.codigo} · achado pelo número do arquivo` : 'o do parque') : null}
              sugestao={v.cone.codigo} aoEscolher={(id, rotulo) => aoMudar({ cone: { id, rotulo } })} />
            {coneTrocado && <span className="dica">Cone trocado: confira de novo para ver se ele estava apto na data.</span>}
            {!coneTrocado && daAnterior.has('cone') && <span className="dica">{DA_ANTERIOR}</span>}
          </div>
          {v.substitui && (
            <Campo rotulo="Motivo da substituição" value={e.substituicao} maxLength={2000} required
              dica={`Substitui a importação de ${data(v.substitui.importadaEm)}: a anterior fica no histórico.`}
              onChange={(x) => aoMudar({ substituicao: x.target.value })} />
          )}
        </div>
      )}

      <Avisos avisos={avisosDoCartao} />
      {deFora && (
        <p className="sub cptu-fora-porque">
          Fica de fora: o resto da conferência deste arquivo não conta. Marque «Importar» para vê-la inteira.
        </p>
      )}
      {falta && <p className="cptu-falta" role="status">{falta}</p>}
    </section>
  );
}
